// helpContent.mjs — 系统使用手册的**内容本体**：结构化的双语数据，纯模块、无 React、无 DOM。
//
// 为什么内容不放 data_content：那棵树是 MirrorMii 的**产品文档**——按岗位的 view glob 授权、
// 走发布管线上线。手册讲的是**本系统怎么用**，必须对每一个登录账号无条件可达（岗位看不到
// 产品文档的人，恰恰最需要知道“为什么那些节点是灰的”）。手册若混进产品文档树，就会同时
// 继承授权闸与发布耦合：改一句话要跑一次发布，而看不到那个 glob 的人永远读不到。
//
// 为什么内容不放 i18n/{zh,en}.mjs：词典是**界面 chrome** 的键值表。长文进词典有两处硬伤——
// (a) 同一段话的中英两版相隔几百行，改一处必漏一处（keys.test.mjs 只能对账“键在不在”，
//     对不上“这两段话说的是不是同一件事”）；(b) 章节的**顺序与层级**是内容的一部分，
//     扁平键值表表达不了，最后一定退化成 help.s1.p3 这种没人敢动的编号。
//     这里一条数据同时握住中英两版，改文案时两版就在眼皮底下。
//
// 为什么数字全部从别处 import / 由 template 拼进正文（不手写）：DP-6——手册若写着一个
// 系统并不执行的数字，就是在为不存在的行为背书，而这种错比没有手册更贵（读者会照着做）。
// 本仓已经为同一个教训付过账：attachments.mjs 的 D3 里，手写的“可传类型”话术在后端加了
// pdf/heic 之后半年没跟上，界面一直在告诉提交方“不收 PDF”。所以：
//   · 附件话术直接复用 attachments.mjs 的 attachHelperText()（由 ATTACH_KINDS 表生成）；
//   · 工时上限从 workDays.mjs 的 DAY_CAP_MINUTES 折算；
//   · 工单类型/状态取自 ticketMeta.mjs；
//   · 其余阈值集中在 HELP_LIMITS，由 helpContent.test.mjs **真的去读** backend 的
//     defaults.h 与 ticket_types.cpp 逐条对账（读 C++ 源核对的做法沿用 attachments.test.mjs）。
import { MAX_ATTACHMENTS, attachHelperText } from './attachments.mjs';
import { STATUS_ORDER, TICKET_TYPES } from './ticketMeta.mjs';
import { DAY_CAP_MINUTES } from './workDays.mjs';
// SB-151：新章的数字同样不手写——排队等待窗口取自 messages.mjs（那里与 defaults.h
// 的 kDmWaitMaxMs/kDmScheduleMaxMs 同值，"最多等多久"与"最多排到多久以后"是同一个数），
// 强读闸的读秒取自闸自己的纯逻辑模块。两者都是纯 .mjs，本模块仍然无 React、无 DOM。
import { DM_SCHEDULE_MAX_MS } from './messages.mjs';
import { READ_COUNTDOWN_SECONDS } from '../components/forcedReadQueue.mjs';
// SB-190 步 5（第二百四十七批）：指派单那条线的两个合同数字（同一工作日几件起顺延、几个
// 工作日内作答）取自钟点条的纯模块——它与 defaults.h 逐条对账，手册不再抄第二遍。
import { SLA_RESPONSE_WORKING_DAYS, SLA_SAME_DAY_THRESHOLD } from './responseSla.mjs';

// ── 后端阈值的镜像 ────────────────────────────────────────────────────────────
// 每一条都在 backend/src/core/defaults.h 里有一个同名常量；helpContent.test.mjs 逐条比对。
// 任何一侧改了数字，测试当场红——手册不会在无人察觉的情况下变成谎话。
export const HELP_LIMITS = {
  codeTtlMinutes: 10,          // kAuthCodeTtlSeconds = 600
  codeCooldownSeconds: 60,     // kAuthCodeCooldownSeconds
  codeDailyCap: 3,             // kAuthCodeDailyCap（按 UTC 日计，且只算**实际发出**的邮件）
  codeMaxAttempts: 5,          // kAuthCodeMaxAttempts
  passwordWindowDays: 7,       // kAuthPasswordWindowDays（OQ-B3 裁决值）
  passwordMaxAgeDays: 90,      // kAuthPasswordMaxAgeDays
  minPasswordLength: 8,        // kAuthMinPasswordLength
  sessionHours: 24,            // kAuthSessionTtlSeconds = 86400
  processingTimeoutDays: 7,    // kTicketProcessingTimeoutDays（T6 超时回到待处理）
  infoRequestTimeoutDays: 14,  // kTicketInfoRequestTimeoutDays（T9 自动撤回）
  autoArchiveDays: 30,         // kTicketAutoArchiveDays（终态满期自动归档）
  attachmentsPerTicket: MAX_ATTACHMENTS,  // = kAttachmentsPerTicket，不再抄第二遍
  // SB-190（第二百四十七批）：响应时限的两个合同数字，取自 responseSla.mjs（不再抄第二遍）。
  slaSameDayThreshold: SLA_SAME_DAY_THRESHOLD,      // kSlaSameDayThreshold（同一工作日超过 3 件顺延）
  slaResponseWorkingDays: SLA_RESPONSE_WORKING_DAYS, // kSlaResponseWorkingDays（1 个工作日内作答）
};

const L = HELP_LIMITS;
// 工时上限是分钟常量，正文要的是小时——折算一次，别在正文里手写 16。
export const HELP_DAY_CAP_HOURS = DAY_CAP_MINUTES / 60;
// 站内信排队的等待窗口，毫秒常量折算成小时——正文里不手写 72。
export const HELP_DM_WAIT_HOURS = DM_SCHEDULE_MAX_MS / 3_600_000;

// ── 工单类型表 ────────────────────────────────────────────────────────────────
// type / 编号前缀 / 是否需要目标文档 / 是否走事故独立流程，四列全部与 backend 的
// ticket_types.cpp 逐行对账（测试会读那个文件）。labelKey 指向**已有**的 i18n 键，
// 手册不为类型名另造一套译名——两套译名迟早对不上，用户看到的是同一个下拉框。
export const HELP_TICKET_TYPES = [
  {
    type: 'bug', prefix: 'BUG', needsDoc: false, incidentMachine: false,
    zh: '系统或产物的行为与预期不符，但还没有造成实际损失。',
    en: 'Something behaves differently from what was specified, but no damage has been done yet.',
  },
  {
    type: 'incident', prefix: 'INC', needsDoc: false, incidentMachine: true,
    zh: '已经造成损失或影响的事件。走另一条独立流程（见下一节），没有“请补充信息”，也不能撤回。',
    en: 'An event that has already caused loss or impact. It runs its own separate flow (see the next section): no information request, and it cannot be withdrawn.',
  },
  {
    type: 'maintenance', prefix: 'MNT', needsDoc: false, incidentMachine: false,
    zh: '日常维护、支持与杂项请求。**拿不准该选哪个类型时就选它**：它不需要目标文档，直接写标题就能提交。',
    en: 'Routine maintenance, support and miscellaneous requests. **Pick this when you are not sure which type fits**: it needs no target document — a title is enough.',
  },
  {
    type: 'tech_debt', prefix: 'TD', needsDoc: false, incidentMachine: false,
    zh: '当下能用、但迟早要还的做法：临时绕过、缺测试、结构欠账。',
    en: 'Something that works today but has to be paid back: a temporary workaround, a missing test, a structural shortcut.',
  },
  {
    type: 'spec_change', prefix: 'TKT', needsDoc: true, incidentMachine: false,
    zh: '对某一篇文档的提改。**唯一需要指定目标文档的类型**——请在文档浏览器打开那篇文档，点“对本文档提改”，目标会自动带进表单。',
    en: 'A proposed change to one document. **The only type that requires a target document** — open the document in the doc browser and click “Propose a change”; the target is carried into the form for you.',
  },
  {
    type: 'self_intro', prefix: 'SI', needsDoc: false, incidentMachine: false,
    zh: '自我介绍初稿：能力罗列、精力分配、自声明的自发责任心归属范围。提交后会通过“请补充信息”逐层追问，你补充后重新提交，作为任务指派与进度预估的依据。',
    en: 'A first draft of your self-introduction: your capabilities, how you allocate effort, and the scope you declare you take spontaneous responsibility for. It is then probed layer by layer through information requests; your answers become the basis for task assignment and progress estimates.',
  },
  {
    type: 'asset', prefix: 'AST', needsDoc: false, incidentMachine: false,
    zh: '素材提交：样本文件作为附件上传（可接收的类型与上限以提交表单的附件栏为准），正文写明来源与选择理由。受托征集时在表单里填写对应的 BL- 工单号。',
    en: 'Sample submission: upload the sample files as attachments (the accepted types and caps are listed under the attachment field of the form), and state their source and why you chose them in the body. If you were asked to collect them, fill in the related BL- work item number in the form.',
  },
  {
    // SB-190（第二百四十五批）：指派单——响应时限的承载。只有审批人／管理员能提；
    // 提交时指定一位受指派人，之后不能改。受指派人只能「响应」，关闭权在发出方。
    type: 'assigned', prefix: 'ASK', needsDoc: false, incidentMachine: false,
    zh: '指派单：由审批人或管理员发出、指名一位受指派人作答的事项，发出时就定下响应方，之后不能再改。受指派人在工单页“响应”（可多次），关闭由发出方或审批人来做；这类工单不进审批队列，也不能撤回。',
    en: 'An assigned ticket: an item issued by an approver or administrator that names one responder, fixed at issue time and never changed afterwards. The responder replies from the ticket page (as often as needed); closing is done by the issuer or an approver. These tickets never enter the approval queue and cannot be withdrawn.',
  },
];

// ── 状态机（成员视角）────────────────────────────────────────────────────────
// 只写提交方**看得到、管得着**的那几步。后端还有拉取序号、回写 CAS 之类的机制，
// 写进手册只会让人以为自己需要做点什么。status 一律取自 ticketMeta.mjs 的 STATUS_ORDER
// （测试校验子集关系），这样 UI 上的彩色状态徽章与手册里的名字必然是同一套。
export const HELP_STATUS_FLOW = [
  {
    status: 'pending',
    zh: '刚提交，排在处理队列里。此时可以补传附件。',
    en: 'Just submitted and queued. You can still add attachments at this point.',
  },
  {
    status: 'processing',
    zh: '工作空间已经把它取走，正在处理。如果超过 ' + L.processingTimeoutDays + ' 天没有结果，系统会自动把它放回“待处理”重新排队——你不需要做任何事。',
    en: 'The workspace has picked it up. If nothing comes back within ' + L.processingTimeoutDays + ' days the system requeues it as “Pending” by itself — you do not have to do anything.',
  },
  {
    status: 'info_requested',
    zh: '处理方需要你补充信息。展开工单，在“补充信息并重新提交”里回答（可以顺便补传附件），然后点“重新提交”回到“待处理”。' + L.infoRequestTimeoutDays + ' 天内没有回应，系统会自动撤回这张工单。',
    en: 'The handler needs more from you. Expand the ticket, answer in “Add information and resubmit” (you may attach files at the same time), then press “Resubmit” to send it back to “Pending”. If you do not respond within ' + L.infoRequestTimeoutDays + ' days the system withdraws the ticket automatically.',
  },
  {
    status: 'accepted',
    // 措辞留了余地：T10 只在“某次发布明确把这张工单标为完成”时才触发，受理本身不保证
    // 下一次发布就带上它。写成“随后就会上线”会让人按错误的时间预期去追问。
    zh: '已受理。等到某次发布把它带上线之后，会转成“已发布”。',
    en: 'Accepted. Once a publish carries it live, it moves on to “Published”.',
  },
  {
    status: 'published',
    zh: '已随发布正式结案（终态）。',
    en: 'Closed with the publish that shipped it (terminal).',
  },
  {
    status: 'rejected',
    zh: '未被采纳（终态）。理由写在“处理反馈”里。',
    en: 'Not accepted (terminal). The reason is written in “Feedback”.',
  },
  {
    status: 'withdrawn',
    zh: '已撤回（终态）。你自己点了“撤回工单”，或者在“待补充信息”里超过 ' + L.infoRequestTimeoutDays + ' 天没有回应而被系统自动撤回。',
    en: 'Withdrawn (terminal). Either you pressed “Withdraw ticket”, or the system withdrew it after ' + L.infoRequestTimeoutDays + ' days without an answer to an information request.',
  },
];

// 事故（INC-）的独立流程。它**不是**上面那条线的分支：没有“请补充信息”，没有撤回，
// 也没有“已拒绝”——唯一的终态是“已复核”。
export const HELP_INCIDENT_FLOW = [
  { status: 'reported', zh: '你上报之后的起始状态。', en: 'The state right after you report it.' },
  { status: 'confirmed', zh: '已被确认并定级。', en: 'Confirmed and graded.' },
  { status: 'handling', zh: '正在处置。', en: 'Being handled.' },
  { status: 'reviewed', zh: '已复核结案（事故的唯一终态）。', en: 'Reviewed and closed (the only terminal state for incidents).' },
];

// 指派单（ASK-）的第三条线（SB-190 §3.1，第二百四十五批落地；钟点条第二百四十七批）。
// 与事故线一样**不是**四类线的分支：没有“请补充信息”、不能撤回、不进审批队列；
// 唯一的终态是“已关闭”，关闭权在发出方／审批人——受指派人永远没有关闭按钮（P1：
// 责任方不控制量自己的那把尺子）。数字全部由 L 拼进去（阈值改了这里跟着变，测试对账）。
// en 的序数词按数生成（4th／2nd），免得阈值一改就印出 "3th"。
const ordinal = (n) => { const s = ['th', 'st', 'nd', 'rd']; const v = n % 100; return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`; };
export const HELP_ASSIGNED_FLOW = [
  {
    status: 'assigned',
    zh: `审批人或管理员发出、指名你作答。响应时钟自发出那一刻起算：${L.slaResponseWorkingDays} 个工作日内作答（次一工作日同一钟点）；非工作日发出的自下一工作日 00:00 起算；同一响应方同一工作日超过 ${L.slaSameDayThreshold} 件的，第 ${L.slaSameDayThreshold + 1} 件起顺延一个工作日。工单详情顶部的钟点条写着截止时刻、套用的规则与“该日第几件”。`,
    en: `Issued by an approver or administrator and naming you as the responder. The response clock starts the moment it is issued: answer within ${L.slaResponseWorkingDays} working day (same wall clock on the next working day); one issued on a non-working day counts from 00:00 of the next working day; and once the same responder has more than ${L.slaSameDayThreshold} on one working day, the ${ordinal(L.slaSameDayThreshold + 1)} and later are deferred by one more working day. The deadline bar at the top of the ticket detail shows the due time, the rule applied and “item #N of that day”.`,
  },
  {
    status: 'responded',
    zh: '你已在“响应这张指派单”里作答（可多次，每次都记入线程）。哪一条算“首次实质响应”由打分方裁定，裁决与理由你在 dc-cli `sla mine` 里看得到。',
    en: 'You have answered in “Respond to this assigned ticket” (as often as needed; every answer joins the thread). Which answer counts as the “first substantive response” is ruled by the scorer; the ruling and its reason are visible to you in dc-cli `sla mine`.',
  },
  {
    status: 'closed',
    zh: '发出方或审批人关闭（指派单唯一的终态）。受指派人不能关闭——量你的那把尺子不在你手里。追问走一张新的指派单并在“派生自”填本单编号。',
    en: 'Closed by the issuer or an approver (the only terminal state for an assigned ticket). The responder cannot close it — the ruler that measures you is not in your hand. A follow-up goes on a new assigned ticket with this number under “Derived from”.',
  },
];

// ── 求助入口 ──────────────────────────────────────────────────────────────────
// 这一块单独导出、单独渲染在页首：手册最该服务的那个人，正是**还不知道该问什么**的人。
// 让他先有一个不需要读完手册就能按的按钮，比把求助方式埋在第十一节更有用。
// 之所以指向“维护（MNT）”：它在 ticket_types.cpp 里 needs_target_doc=false，是不需要
// 任何前置知识就能提交的类型；而“类型选错了怎么办”由既有的“请补充信息”回路兜底——
// 这不是我们为手册新造的承诺，是系统本来就在跑的那条回路。
export const HELP_ASK = {
  to: '/submit',
  title: { zh: '不知道该问谁？提一张工单就行', en: 'Not sure who to ask? Just file a ticket' },
  lead: {
    zh: '不用先学会这套工单分类。遇到任何卡住的地方——功能不会用、看不到某份文档、数据看着不对——都可以直接提一张“维护（MNT）”工单：它不需要目标文档，把问题写进标题即可。',
    en: 'You do not need to learn the ticket taxonomy first. Whenever you are stuck — you cannot work out a feature, a document will not open, a number looks wrong — file a “Maintenance (MNT)” ticket: it needs no target document, just put the question in the title.',
  },
  cta: { zh: '去提交一张工单', en: 'Go file a ticket' },
  notes: [
    {
      zh: '写清楚三件事就够了：你在哪个页面、你做了什么、你期待看到什么。',
      en: 'Three things are enough: which page you were on, what you did, and what you expected to see.',
    },
    {
      zh: '类型选错了不要紧。处理方会把工单转成“待补充信息”来问你，你补充后重新提交即可——不需要撤回重交。',
      en: 'Picking the wrong type is fine. The handler moves the ticket to “Info requested” and asks; you answer and resubmit — no need to withdraw and start over.',
    },
    {
      zh: '如果你根本登录不进来（收不到验证码、或已经用完当天的发送次数），请直接联系管理员：管理员可以清除你当天的发信限额。',
      en: 'If you cannot sign in at all (no code arrives, or you have used up the day’s sends), contact an administrator directly — an administrator can clear your send quota for the day.',
    },
  ],
};

// ── 章节 ──────────────────────────────────────────────────────────────────────
// 块的形状（渲染侧只认这几种，JSX 保持薄）：
//   { k: 'p',    zh, en }              段落
//   { k: 'ul',   items: [{zh,en}] }    无序列表
//   { k: 'ol',   items: [{zh,en}] }    有序列表（步骤）
//   { k: 'note', zh, en }              浅色提示条
//   { k: 'warn', zh, en }              醒目提示条（不可逆/易误解的事）
//   { k: 'link', to, zh, en }          站内跳转按钮
//   { k: 'ticketTypes' } / { k: 'statusFlow' } / { k: 'incidentFlow' } / { k: 'attachLimits' }
//                                      由上面的表实时生成，手册不另存一份文字
export const HELP_SECTIONS = [
  {
    id: 'signin',
    title: { zh: '一、登录', en: '1. Signing in' },
    blocks: [
      {
        k: 'p',
        zh: '本系统仅限受邀协作者，请使用邀请你的那个邮箱地址登录。登录页有两个页签：验证码与密码。',
        en: 'The system is invitation-only: sign in with the email address you were invited on. The sign-in page has two tabs — “Login code” and “Password”.',
      },
      {
        k: 'ol',
        items: [
          {
            zh: '验证码登录（任何时候都可用）：填邮箱 → 点“发送验证码” → 到邮箱里取 6 位数字 → 填进“验证码”框 → 点“登录”。',
            en: 'Code sign-in (always available): enter your email, press “Send code”, take the 6-digit code from your inbox, type it into “Login code” and press “Sign in”.',
          },
          {
            zh: '密码登录（可选的便捷通道）：先设置密码，见下。',
            en: 'Password sign-in (an optional convenience channel): set a password first — see below.',
          },
        ],
      },
      {
        k: 'ul',
        items: [
          {
            zh: `验证码 ${L.codeTtlMinutes} 分钟内有效；连续输错 ${L.codeMaxAttempts} 次即作废，重新发一封即可。`,
            en: `A code is valid for ${L.codeTtlMinutes} minutes; after ${L.codeMaxAttempts} wrong tries it dies — just request a new one.`,
          },
          {
            zh: `两次发送之间要等 ${L.codeCooldownSeconds} 秒；每个邮箱每天最多发出 ${L.codeDailyCap} 封（只计算真正发出去的邮件）。`,
            en: `You must wait ${L.codeCooldownSeconds} seconds between sends; at most ${L.codeDailyCap} mails go out per address per day (only mails actually sent are counted).`,
          },
          {
            zh: '页面永远显示“若邮箱有效，验证码已发送”——它**故意**不告诉你这个邮箱在不在系统里。没收到邮件时请先翻垃圾邮件箱，再确认用的是邀请邮箱。',
            en: 'The page always says “If the address is valid, a code has been sent” — it deliberately never reveals whether the address exists here. If nothing arrives, check your spam folder first, then check that you used the invited address.',
          },
          {
            zh: `登录后会话有效期约 ${L.sessionHours} 小时，之后需要重新登录。`,
            en: `A session lasts about ${L.sessionHours} hours; after that you sign in again.`,
          },
        ],
      },
      {
        k: 'p',
        zh: '密码通道：点右上角头像 →“设置密码”。密码至少 ' + L.minPasswordLength + ' 个字符。',
        en: 'The password channel: open the avatar menu at the top right and choose “Set a password”. A password needs at least ' + L.minPasswordLength + ' characters.',
      },
      {
        k: 'note',
        zh: `密码不是独立的登录方式，而是**验证码之后的 ${L.passwordWindowDays} 天窗口**：在最近一次验证码登录后的 ${L.passwordWindowDays} 天内，可以直接用密码登录；超过 ${L.passwordWindowDays} 天就要再收一次验证码。另外密码超过 ${L.passwordMaxAgeDays} 天未更换会停用密码通道。这两件事都只影响密码——验证码登录始终可用，任何情况下都不会把你锁在门外。`,
        en: `A password is not an independent way in — it is a ${L.passwordWindowDays}-day window that opens after a code sign-in: within ${L.passwordWindowDays} days of your last code sign-in you may sign in with the password alone; past that you need one more code. A password older than ${L.passwordMaxAgeDays} days also stops working on the password channel. Both only ever affect the password: code sign-in always works, so nothing here can lock you out.`,
      },
    ],
  },

  {
    id: 'docs',
    title: { zh: '二、文档：目录树与灰掉的节点', en: '2. Documents: the tree and the greyed-out nodes' },
    blocks: [
      {
        k: 'p',
        zh: '“文档”页左边是目录树，右边是正文。点文件夹展开或折叠，点文档打开正文；工具条上有“全部展开 / 全部折叠”。窄屏上用“展开目录 / 收起目录”在树和正文之间切换。',
        en: 'The Docs page has the tree on the left and the document on the right. Click a folder to expand or collapse it, click a document to open it; the toolbar has “Expand all / Collapse all”. On a narrow screen use “Show contents / Hide contents” to switch between the tree and the text.',
      },
      {
        k: 'warn',
        zh: '看到灰掉的节点不要当成故障——它有两种，含义完全不同：',
        en: 'A greyed-out node is not a fault. There are two kinds and they mean different things:',
      },
      {
        k: 'ul',
        items: [
          {
            zh: '**整行变淡**：这篇文档存在，但你的岗位无权查看它的正文。点开会明确写着“该文档存在，但你的岗位无权查看其内容”。这是刻意的设计：让你知道有这么一份东西、可以据此提问，但不给正文。这不是 bug，也不是权限配错了。',
            en: '**The whole row is dimmed**: the document exists, but your job post may not read it. Opening it says exactly that — “This document exists, but your job post cannot view its contents.” That is deliberate: you get to know it exists and can ask about it, you just do not get the text. It is not a bug and not a misconfiguration.',
          },
          {
            zh: '**标题灰、带“待撰写”标签**：这一篇还没有写正文，但可以点开——它是目录里预留的位置。',
            en: '**A grey title with a “To be written” badge**: this one has not been written yet, but it is still clickable — it is a placeholder the outline reserves.',
          },
          {
            zh: '还有第三种情况你看不到任何痕迹：有些子树在服务端就被整段裁掉，根本不会出现在树里。所以“树里没有”不等于“不存在”。',
            en: 'A third case leaves no trace at all: some subtrees are pruned server-side and never reach the tree. So “not in the tree” does not mean “does not exist”.',
          },
        ],
      },
      {
        k: 'p',
        zh: '你能看到什么，由管理员给你设定的**岗位**决定（头像菜单里能看到自己的岗位）。需要某一份文档的访问权时，提一张工单说明理由即可。',
        en: 'What you can see is decided by the **job post** an administrator assigned to you (the avatar menu shows yours). If you need access to a particular document, file a ticket saying why.',
      },
      {
        k: 'ul',
        items: [
          {
            // 第一百二十三批订正：缺当前语言时不再只给一个切换按钮——直接展示真实
            // 存在的那一版正文并加提示（外来单语组在 en 界面下直给 zh 原文）。
            zh: '语言：右上角的 EN / 中文 按钮同时切换界面与文档语言。某篇文档没有当前语言的版本时，页面会直接显示**真实存在的那一版**正文并加一句提示（外来组的文档多数只有中文原文，英文界面下也直接给中文原文）；另一侧语言真的存在时才会有“切换到…”按钮。系统不会编造译文。',
            en: 'Language: the EN / 中文 button at the top right switches both the interface and the documents. When a document has no version in the current language, the page shows **the version that actually exists**, with a notice (foreign groups’ documents mostly exist only in their original, which is shown as-is even under the English interface); a “Switch to …” button appears only when the other language really exists. The system never fabricates a translation.',
          },
          {
            zh: '文档按项目组分树：你属于多个组时，切换当前组（见“组切换”一节）就换一棵树。',
            en: 'Documents are one tree per project group: if you belong to several groups, switching the current group (see the “Switching groups” section) switches the tree.',
          },
          {
            zh: '正文里的站内链接可以直接点，前进/后退也照常工作；地址栏里的 URL 就是这篇文档的固定链接，可以直接分享给同样有权限的人。',
            en: 'In-document links work, and so do Back/Forward; the URL in the address bar is that document’s permanent link and can be shared with anyone who has the same access.',
          },
          {
            zh: '“对本文档提改”按钮会跳到提交页，类型自动选为“规格变更”，目标文档也一并带过去——这是提改的正路，不必手抄文档编号。',
            en: '“Propose a change” jumps to the submit page with the type preset to “Spec change” and the target document carried along — that is the intended route; you never need to copy a document id by hand.',
          },
          {
            zh: '“修改历史”只在该项目组开启了文档改动追踪之后才有记录，且不回溯开启之前的历史。看到“该项目组尚未开启文档改动追踪”属于正常。',
            en: '“Change history” only has entries once change tracking has been switched on for that project group, and it is never back-filled for the period before. Seeing “change tracking is not enabled for this project group” is normal.',
          },
        ],
      },
      { k: 'link', to: '/docs', zh: '打开文档', en: 'Open Docs' },
    ],
  },

  {
    id: 'ticket-types',
    title: { zh: '三、提交工单：八种类型分别是什么', en: '3. Filing a ticket: what the eight types mean' },
    blocks: [
      {
        k: 'p',
        zh: '在“提交 Ticket”页填表。除类型外还要选项目组与提交语言（按你实际书写的语言选，默认跟当前界面语言），标题必填，描述强烈建议填。提交成功后会给出工单编号，每种类型有自己的编号前缀：',
        en: 'Fill in the form on the “Submit Ticket” page. Besides the type you pick a project group and the language you are writing in (it follows the interface language by default). The title is required; the body is strongly recommended. On success you get a ticket number — each type has its own prefix:',
      },
      { k: 'ticketTypes' },
      {
        k: 'note',
        zh: '只有“规格变更”需要指定目标文档，其余七种直接提交即可（“指派单”另需指定受指派人，且只对审批人与管理员开放）。如果提交时看到“当前职位无提交 ticket 权限”，说明你的岗位没有开这项权限，请联系管理员。',
        en: 'Only “Spec change” needs a target document; the other seven can be submitted as they are (an “assigned” ticket additionally names a responder, and only approvers and administrators may issue one). If you are told your job post may not submit tickets, that right is not enabled for your post — ask an administrator.',
      },
      { k: 'link', to: '/submit', zh: '去提交', en: 'Go to Submit' },
    ],
  },

  {
    id: 'lifecycle',
    title: { zh: '四、提交之后会发生什么', en: '4. What happens after you submit' },
    blocks: [
      {
        k: 'p',
        zh: '在“我的提交”页可以看到自己的全部工单，按状态分组；点开一行能看到正文、处理反馈、附件与状态时间线。状态的含义如下：',
        en: 'The “My Tickets” page lists everything you filed, grouped by status; expanding a row shows the body, the handler’s feedback, the attachments and the status timeline. The statuses mean:',
      },
      { k: 'statusFlow' },
      {
        k: 'warn',
        zh: '**撤回是终止操作，不可恢复。**工单就此结案、工单号作废，请补信息的对话线索和已上传的附件都不再继续。如果你只是想补材料，请不要撤回：在同一张工单里上传附件后点“重新提交”即可（见下一节）。',
        en: '**Withdrawing is terminal and cannot be undone.** The ticket is closed for good, its number is spent, and the information-request thread and the files you already uploaded go nowhere. If you only want to add material, do not withdraw: upload into the same ticket and press “Resubmit” (see the next section).',
      },
      {
        k: 'ul',
        items: [
          {
            zh: '页面上的“撤回工单”按钮出现在“待补充信息”状态的工单详情里，紧挨着“重新提交”——它是“我放弃回答”的意思，不是“我要改一下”的意思。',
            en: 'The “Withdraw ticket” button appears in the detail panel of a ticket that is awaiting information, right next to “Resubmit”. It means “I give up on answering”, not “I want to adjust something”.',
          },
          {
            zh: `事故（INC-）走的是另一条线：${HELP_INCIDENT_FLOW.map((s) => s.status).length} 个状态，没有“请补充信息”、不能撤回、也没有“已拒绝”。`,
            en: `Incidents (INC-) run a separate line of ${HELP_INCIDENT_FLOW.map((s) => s.status).length} states: no information request, no withdrawal, and no rejection.`,
          },
          {
            zh: `指派单（ASK-）走第三条线：${HELP_ASSIGNED_FLOW.length} 个状态，只有“响应”和“关闭”两个动作，不进审批队列。`,
            en: `Assigned tickets (ASK-) run a third line of ${HELP_ASSIGNED_FLOW.length} states with only two actions, “Respond” and “Close”, and never enter the approval queue.`,
          },
        ],
      },
      { k: 'incidentFlow' },
      { k: 'assignedFlow' },
      {
        k: 'ul',
        items: [
          {
            zh: `工单进入终态满 ${L.autoArchiveDays} 天后会自动归档。归档的工单默认不在列表里——打开“显示已归档”开关就能看到，它们没有丢。`,
            en: `A ticket is archived automatically ${L.autoArchiveDays} days after it reaches a terminal state. Archived tickets are hidden from the list by default — switch on “Show archived” and they are all still there.`,
          },
          {
            zh: '状态变化会通过右上角的铃铛通知你（受理、驳回、请你补充信息、超时自动撤回、随发布结案等）。点通知条目会直接跳到对应的工单。',
            en: 'Status changes reach you through the bell at the top right (accepted, rejected, information requested, auto-withdrawn after a timeout, closed with a publish, …). Clicking a notification takes you straight to the ticket.',
          },
        ],
      },
      { k: 'link', to: '/my-tickets', zh: '查看我的提交', en: 'Open My Tickets' },
    ],
  },

  {
    id: 'attachments',
    title: { zh: '五、附件：提交之后依然可以补传', en: '5. Attachments: you can still add them after submitting' },
    blocks: [
      {
        k: 'warn',
        // 第三百六十二批：补上指派单那一格（后端第三百五十九批起开，页面本批跟上）。
        zh: '这一条被误解过、并且付出过代价：**工单建好之后仍然可以补传附件**，不需要撤回重交。在“我的提交”里展开那张工单，“补传附件”区就在“重新提交”上方。工单处于“待处理”或“待补充信息”时都可以传。指派单（ASK-）不同：只有发出方能补传，且只到受指派人首次作答之前；受指派人不能传。',
        en: 'This one has been misread before, at a cost: **you can still add attachments after the ticket exists** — there is no need to withdraw and file a new one. Expand the ticket in “My Tickets”; the “Add attachments” box sits just above “Add information and resubmit”. It works while the ticket is pending or awaiting information. Assigned tickets (ASK-) differ: only the issuer can add attachments, and only until the assignee first responds; the assignee cannot.',
      },
      {
        k: 'ol',
        items: [
          { zh: '在“我的提交”里点开那张工单。', en: 'Open the ticket in “My Tickets”.' },
          { zh: '在“补传附件”区点“选择文件”，挑好之后点“上传附件”。', en: 'In “Add attachments”, press “Choose files”, then press “Upload attachments”.' },
          {
            zh: '如果工单处于“待补充信息”，上传完再点“重新提交”，工单才会回到处理队列；只是补材料而没有被要求补充信息时，传完即可。',
            en: 'If the ticket is awaiting information, press “Resubmit” after the upload so it goes back into the queue; if nobody asked you for anything, uploading is enough.',
          },
        ],
      },
      { k: 'attachLimits' },
      {
        k: 'ul',
        items: [
          {
            zh: `每张工单最多 ${L.attachmentsPerTicket} 个附件，且是按**整张工单已有的数量**封顶的：已经传了 7 个，就只剩 3 个额度。补传面会直接告诉你还能传几个。`,
            en: `A ticket takes at most ${L.attachmentsPerTicket} attachments, counted against **everything already on it**: with 7 uploaded you have 3 left. The upload box tells you the remaining quota.`,
          },
          {
            zh: '挑选时超限或类型不收的文件会当场标红并写明原因（哪个文件、多大、上限多少），不会跟着提交。',
            en: 'A file that is too large or of an unaccepted type is flagged the moment you pick it, with the reason spelled out (which file, how big, what the limit is); it is not sent.',
          },
          {
            zh: '部分文件上传失败时，失败的那些会留在列表里等你重试或移除——**不要**因此撤回工单再交一张新的。',
            en: 'If some files fail, they stay in the list so you can retry or remove them — do **not** withdraw the ticket and file a new one because of it.',
          },
        ],
      },
    ],
  },

  {
    id: 'work',
    title: { zh: '六、工作项与工时', en: '6. Work items and work hours' },
    blocks: [
      {
        k: 'p',
        zh: '“工作项”页列出当前项目组处于待办 / 进行中的工作项（BL- 编号），可以只看与自己相关的那些；点开条目看详细说明。',
        en: 'The “Work items” page lists the pending / in-progress work items (BL- numbers) of your project group, with a filter for the ones related to you; expand an entry for the details.',
      },
      {
        k: 'p',
        zh: '“工时”页用来登记自己的工时。**只有岗位开了这项权限的账号才能登记**；没有的话页面会明确写着“你的岗位没有登记工时的权限”，这不是故障。',
        en: 'The “Work hours” page is where you log your own hours. **Only accounts whose job post carries that right can log**; without it the page says so plainly — that is not a fault.',
      },
      {
        k: 'ul',
        items: [
          {
            zh: '页面顶部的日期条**就是**可登记的窗口：条上没有的日子登记不了（今天前后各几天，具体天数由服务端给出）。',
            en: 'The day strip at the top **is** the entry window: a day that is not on the strip cannot be logged (a few days either side of today; the exact span comes from the server).',
          },
          { zh: '时长必须是 0.5 小时的整数倍。', en: 'Hours must be a whole multiple of 0.5.' },
          {
            zh: `同一天所有工单合计不能超过 ${HELP_DAY_CAP_HOURS} 小时。`,
            en: `All tickets on one day may not add up to more than ${HELP_DAY_CAP_HOURS} hours.`,
          },
          {
            zh: '只能登记到“待办 / 进行中”的工单上；下拉框里没有的工单，就是规则不接受的（已结案的、以及摩擦内建单）。',
            en: 'You can only log against pending / in-progress work items; anything the rules refuse (closed items, and friction items) is simply not in the picker.',
          },
          {
            // R-08 订正（第一百二十四批审查补）：规划行与投入行的两条路是互斥的，
            // 只写后一半会让录错一笔计划的人以为自己走进了死路。
            zh: '**规划**（还没投入的计划行）在到期之前可以原地编辑或删除；**已投入**的记录则不能原地修改，只能追加一条“冲正”把多记的时长抵扣掉（可以少于原时长）。这是刻意的：原始投入必须留痕。',
            en: '**Planned** rows (not yet invested) can be edited or deleted in place until their day arrives; an **invested** entry cannot be edited in place — you add a reversal that deducts the excess instead (it may be less than the original). This is deliberate: the original record has to stay visible.',
          },
        ],
      },
      { k: 'link', to: '/work-hours', zh: '打开工时', en: 'Open Work hours' },
    ],
  },

  {
    id: 'cli',
    title: { zh: '七、命令行与 API 密钥', en: '7. The command line and API keys' },
    blocks: [
      {
        k: 'p',
        zh: '除了这个网页，本系统还有一个命令行客户端 `dc-cli`：查文档、提交与回复工单、看工作项、记工时与冲正、读通知、把规格书里的图版取到本地。它和网页做的是**同一件事**——同样的权限、同样的规则、同样的编号。',
        en: 'Besides this web interface the system has a command-line client, `dc-cli`: browse documents, file and answer tickets, read work items, log and reverse hours, read notifications, and download the images from the specifications. It does **the same things** as this page — same permissions, same rules, same numbers.',
      },
      {
        k: 'note',
        zh: '**你不一定需要它。** 如果你一直在浏览器里工作，这一节可以跳过。它是给「手边没有浏览器会话」的场景准备的：在另一台机器上、在自动化脚本里、或者由一个替你干活的 AI 助手调用。',
        en: '**You may not need it.** If you always work in a browser, skip this section. It exists for the case where no browser session is at hand: on another machine, inside an automation script, or driven by an AI assistant working on your behalf.',
      },
      {
        k: 'p',
        zh: '命令行用的凭据叫 **API 密钥**，不是你的登录密码。关于它，有四件事必须先知道：',
        en: 'The command line authenticates with an **API key**, not with your sign-in password. Four things to know about it first:',
      },
      {
        k: 'ul',
        items: [
          {
            zh: '**密钥由管理员签发，不能自助生成。** 需要就去找管理员，说明你要在哪里用。一枚能自助生成的密钥等于一条绕开登录流程的第二条入口，而登录流程上挂着验证码、设备窗口、口令年龄这一整套判据。',
            en: '**Only an administrator can issue a key — you cannot generate one yourself.** Ask for one and say where you will use it. A self-service key would be a second way in that bypasses the sign-in flow, and that flow is where the login codes, device windows and password-age rules live.',
          },
          {
            zh: '**明文只出现一次。** 签发那一刻页面上会显示一串 `dck_` 开头的字符，那是它在世上唯一的一份——系统里只存它的哈希。当场复制保存好；关掉那个提示之后，谁也拿不回来。',
            en: '**The plaintext is shown exactly once.** At issue time the page shows a string starting with `dck_`; that is the only copy in existence — the system stores only its hash. Copy it there and then; once the notice is dismissed nobody can retrieve it.',
          },
          {
            zh: '**丢了就找管理员吊销重签**，不要去猜、也不要试图从系统里读回来（读不到）。吊销是即时生效的：下一次请求就失效。',
            en: '**If you lose it, ask an administrator to revoke and re-issue.** Do not guess it and do not try to read it back (you cannot). Revocation takes effect immediately — the very next request fails.',
          },
          {
            zh: '**密钥只到得了成员面。** 用它做不了管理与审批动作（那两块只在网页上、需要真正的登录会话）。被拒时会明确告诉你「这把密钥是好的，被拒的是这个动作」，不要因此去重签一枚。',
            en: '**A key only reaches the member surface.** Administrative and approval actions are refused (those live on the web page and need a real sign-in session). The refusal says explicitly that the key is fine and the ACTION is what was refused — do not re-issue a key because of it.',
          },
        ],
      },
      {
        k: 'warn',
        zh: '密钥等价于你的会话：拿到它的人能以**你的身份**提交工单、记工时。别贴进聊天记录、别提交进 git、别放在共享目录里。工具的 `init` 会检查配置文件是否被 git 跟踪，但它只看得到自己那一个文件。',
        en: 'A key is equivalent to your session: whoever holds it can file tickets and log hours **as you**. Do not paste it into chat logs, commit it to git, or leave it in a shared directory. The tool\'s `init` checks whether its own config file is tracked by git — but that is the only file it can see.',
      },
      {
        k: 'p',
        zh: '管理员签发密钥的位置在管理面的「API 密钥」页（管理面地址是 `/admin`，密钥页是 `/admin/api-keys`）——这一页只有管理员打得开；写在这里是为了让你说得出要找的是哪一页。工具本身在仓库的 `tools/dc-cli/` 下，起步步骤见那里的 README：先 `init` 写配置、把密钥填进去，再 `whoami` 验证。',
        en: 'Administrators issue keys on the admin "API keys" page (`/admin/api-keys`, under `/admin`). Only administrators can open it; it is named here so you can say which page you mean. The tool itself lives at `tools/dc-cli/` in the repository, and its README has the getting-started steps: run `init`, put the key in the config, then `whoami` to verify.',
      },
      {
        k: 'note',
        zh: '经命令行做的写入会**自动**在记录里标注「经工具发生」，并记下是哪个工具。这不是你需要填的东西，也不是你能关掉的东西——它由凭据本身推导，为的是日后看记录时分得清「这是本人在页面上做的」还是「脚本代做的」。',
        en: 'Writes made through the command line are **automatically** marked in the record as "made through a tool", together with which tool. It is not something you fill in and not something you can switch off: it is derived from the credential itself, so that later readers can tell a person acting on the page from a script acting for them.',
      },
    ],
  },

  {
    id: 'account',
    title: { zh: '八、账号、通知与语言', en: '8. Account, notifications and language' },
    blocks: [
      {
        k: 'ul',
        items: [
          {
            zh: '右上角头像菜单里有：你的昵称与邮箱、你的**岗位**（决定你能看哪些文档、能做哪些事）、编辑昵称、设置/修改密码、登出。',
            en: 'The avatar menu at the top right shows your display name and email, your **job post** (which decides what you can see and do), and lets you edit your display name, set or change your password, and sign out.',
          },
          {
            zh: '昵称留空就显示邮箱。岗位不能自己改，需要调整请联系管理员。',
            en: 'Leave the display name empty and your email is shown instead. You cannot change your own job post — ask an administrator.',
          },
          {
            zh: '铃铛是未读通知；点条目直接跳到对应的工单或工作项，也可以“全部已读”。',
            en: 'The bell holds unread notifications; clicking one jumps to the ticket or work item, and “Mark all read” is there too.',
          },
          {
            zh: '语言按钮切换整个系统（界面 + 文档 + 本手册），选择会记在这台设备上。',
            en: 'The language button switches everything (interface, documents and this manual); the choice is remembered on this device.',
          },
          {
            zh: '窄屏上左上角的 ≡ 打开导航抽屉，所有入口都在里面。',
            en: 'On a narrow screen the ≡ at the top left opens the navigation drawer; every entry lives there.',
          },
        ],
      },
    ],
  },

  // ── SB-151 第一百二十五批：以下六章覆盖第一百零九～一百二十四批的成员面功能 ──
  // 纪律与前八章相同：写给会用系统但不懂内部实现的人；"会咬人"的语义（打开即已读、
  // 取件即焚、撤回边界）用 warn 讲在前面；数字一律 import，不手写。
  {
    id: 'messages',
    title: { zh: '九、站内信', en: '9. Direct messages' },
    blocks: [
      {
        k: 'p',
        zh: '“站内信”是一对一的信，分收件箱与发件箱。写信时除了收件人（填对方邮箱）与正文，还必须回答一个问题：**这封信关于什么**——从你们的共同引用里选一个（同一个组、某个工作项、某份文档，或他之前发给你的信）。收件人要看得见你凭什么给他写信；没有共同引用，这封信发不出去。',
        en: 'Direct messages are one-to-one, with an inbox and an outbox. Besides the recipient (their email address) and the body, you must answer one question: **what is this message about** — pick one shared reference (the group you share, a work item, a document, or a message they sent you earlier). The recipient gets to see why you are writing to them; with no shared reference the message cannot be sent.',
      },
      {
        k: 'warn',
        zh: '**点开正文的那一刻这封信就算已读**：对方会收到已读提醒，并且从此再也不能撤回它。所以打开一封未读的来信之前，页面会先问你一句确认；看自己的发件箱、或重看一封已读过的信，不弹这一问。',
        en: '**The moment you open the body, the message counts as read**: the sender receives a read receipt and can never withdraw it again. That is why the page asks for one confirmation before you open an unread incoming message; looking at your own outbox, or re-reading an already-read message, never triggers that question.',
      },
      {
        k: 'ul',
        items: [
          {
            zh: `排队防刷屏：对同一个人，一次只能有一封在途的信；对方还没读你上一封时，新的一封只能排队，等他读完自动发出。${HELP_DM_WAIT_HOURS} 小时内他仍没读，排队的这封就不会发出，你会收到提醒。因排队而迟发的信带**曝光标记**——“这封信等到对方读了上一封才发出”这件事，双方都看得见。`,
            en: `Queueing keeps the channel civil: only one message per recipient can be in flight at a time, and while they have not read your previous one a new message can only queue, going out automatically once they read it. If they still have not read it within ${HELP_DM_WAIT_HOURS} hours, the queued message is not sent and you are notified. A message delayed by the queue carries an **exposure mark** — both sides can see that it waited for the previous one to be read.`,
          },
          {
            zh: '**撤回只在对方打开之前有效**。还没发出的信撤回后无痕；已发出但未读的信撤回会清掉正文（系统不留副本），但对方仍会看到一条“已撤回”的记录——来自谁、关于什么、什么时候。对方已经读了的信，撤不回。',
            en: '**Withdrawing only works before the recipient opens the message.** Withdrawing one that has not gone out yet leaves no trace; withdrawing one that was sent but not yet read erases the body (no copy is kept), but the recipient still sees a “withdrawn” row — from whom, about what, and when. Once it has been read, it cannot be withdrawn.',
          },
          {
            zh: '带“系统信”标记的信不是某个人手写的：那是管理员经审定模板发出的通知（例如命令行接入指引）。它不排队，但和普通信一样要读。',
            en: 'A message with the “system” badge was not written by a person: it is a notice an administrator sent through a reviewed template (a command-line onboarding guide, for example). It skips the queue, but you read it like any other message.',
          },
          {
            zh: '打开一封来信后，详情框里有“回复”：收件人与“关于”会自动带上并锁定，你只写正文。**回复只能回你已经读过的信**——先打开、再回复；把一封你没读过的信当作回复对象，服务端会拒绝。',
            en: 'After you open an incoming message, its detail dialog offers “Reply”: the recipient and the “about” anchor are filled in and locked, you only write the body. **You can only reply to a message you have read** — open first, then reply; a reply anchored to something you never read is refused by the server.',
          },
          {
            zh: '一封回信的详情里可以“展开引用原文”，一层层看到整条往来。展开只是看：它**不会**把被引用的那封标成已读，也不发出任何回执。',
            en: 'Inside a reply you can “Expand the quoted original”, unfolding the exchange layer by layer. Expanding is just looking: it does **not** mark the quoted message as read, and it sends no receipt.',
          },
          {
            zh: '列表里除了“未读/已读”，一封信还可能带上**“已回复”**标记：在收件箱它读作“我回过这封”，在发件箱读作“对方回过这封”。它是**并排的一位事实、不是第六种状态**——一封信可以既是“已读”又是“已回复”。判定只认经“回复”按钮建立的那条关联，且回信必须真的发出去了（排队中不算、撤回的不算）。收件箱上方的“只看未回复”开关，回答的是“谁还欠我回信”。',
            en: 'Beyond “unread/read”, a message may also carry a **“Replied”** mark: in the inbox it means “I replied to this one”, in the outbox “they replied to this one”. It is **a fact standing beside the state, not a sixth state** — a message can be both “Read” and “Replied”. Only a link created through the Reply button counts, and the reply must actually have gone out (queued does not count, withdrawn does not count). The “Unanswered only” switch above the inbox answers the question “who still owes me a reply”.',
          },
          {
            zh: '“只看未回复”是按**当前这一页**过滤的：信很多时，它说的是“这一页里没有未回复的”，不是“整个箱子都回完了”。',
            en: 'The “Unanswered only” filter works over **the page you are looking at**: when you have many messages it tells you “nothing on this page is unanswered”, not “the whole box is answered”.',
          },
          {
            zh: '收件人可以把一封已读的来信“交给管理员”（举报通道）：交出的只有那一封的正文，交出**不可撤销**，发送方不会被通知。默认情况下管理员读不到任何人的站内信——这是唯一的例外，而它握在收件人手里。',
            en: 'A recipient may hand one received and read message over to the administrators (the report channel): only that one message is handed over, the act is **irreversible**, and the sender is not notified. By default administrators cannot read anyone’s messages — this is the only exception, and it sits in the recipient’s hands.',
          },
        ],
      },
      { k: 'link', to: '/messages', zh: '打开站内信', en: 'Open Messages' },
    ],
  },

  {
    id: 'forced-read',
    title: { zh: '十、强读闸：为什么整屏拦住你', en: '10. The forced-read gate: why the whole screen is blocked' },
    blocks: [
      {
        k: 'p',
        zh: `有未读站内信时，你在页面之间跳转的那一刻会被一个全屏窗口拦住，带你把未读的信逐封读完（旧的在前）：滚动到正文末尾，等 ${READ_COUNTDOWN_SECONDS} 秒读秒走完，再点「确认已读」进入下一封；全部读完自动放行。正文不满一屏的短信视同已到末尾，读秒直接开始。**标已读只在你按下确认那一刻发生**——系统不会替你点，你也不会因为挂机而被记成读完了七封信。`,
        en: `While you have unread direct messages, the moment you navigate between pages a full-screen window stops you and walks you through them one by one, oldest first: scroll to the end of the body, wait out the ${READ_COUNTDOWN_SECONDS} seconds, then press the confirm button to move on; once all are read you are released automatically. A body shorter than one screen counts as already at the end, so its countdown starts right away. **Nothing is marked read until you press that button** — the system never presses it for you, so walking away from your desk can never leave you recorded as having read seven messages.`,
      },
      {
        k: 'note',
        zh: '为什么要拦：站内信是“必须送达”的通道，排队机制按“上一封被读了”逐封放行；没有任何东西保证“被读到”，整条通道就转不动。这道闸对**所有**登录账号生效，没有豁免；它只在页面跳转时检查——正在填的表单不会被一封刚到的信打断。',
        en: 'Why the gate exists: direct messages are a must-arrive channel, and the queue releases each next message only after the previous one was read; with nothing guaranteeing “actually read”, the whole channel stalls. The gate applies to **every** signed-in account, with no exemption; and it only checks when you navigate — a form you are filling in is never interrupted by a message that just arrived.',
      },
      {
        k: 'ul',
        items: [
          {
            zh: '从邮件里的一次性登录链接直登进来的账号，读完站内信后系统还会带你逐条清完未读的铃铛通知：页面会自己跳到每条通知的目标页并弹出说明——这不是出错。全部清完会明确告诉你已恢复自由。',
            en: 'If you signed in through a one-time login link from an email, then after the messages are cleared the system also walks you through your unread bell notifications one by one: the page navigates itself to each notification’s target and shows an explanation — that is not a malfunction. When everything is cleared, it tells you plainly that you are free to browse again.',
          },
          {
            zh: '一次查询只取得到有限条未读，所以读完手上这一批之后闸会**再查一次**：还有就接着拦，直到某一次查回来一封不剩才放行（未读通知那条链同理）。这中间会短暂显示“正在确认是否还有未读的信”——不是卡住了。',
            en: 'One query only returns a limited number of unread items, so after you finish the batch in hand the gate **checks again**: if more are waiting it keeps going, and it only releases you once a check comes back empty (the notification chain works the same way). You may briefly see “checking whether anything else is still unread” — that is not a hang.',
          },
          {
            zh: '清欠链上“标记已读”连续失败满 3 次会出现“暂缓清欠”按钮：欠账原样保留在服务端，本次先放你走，下次直登或刷新时继续。正文加载失败或标已读失败都可以当场重试——闸不会静默跳过任何一封。',
            en: 'If marking a notification read fails 3 times in a row during that cleanup, a “defer” button appears: the debt stays untouched on the server, you are let go for now, and the walk resumes on your next direct sign-in or refresh. A failed body load or a failed mark-as-read can be retried on the spot — the gate never silently skips an item.',
          },
        ],
      },
    ],
  },

  {
    id: 'secrets',
    title: { zh: '十一、密件：取件即焚', en: '11. Secret deliveries: take once, and it burns' },
    blocks: [
      {
        k: 'p',
        zh: '“密件”页用来点对点接收凭据（API 密钥、口令等）。每件密件是两种基本形态之一：**一次性**或**限时**；一次性件还可以同时带一个过期时刻（列表上显示为“一次性 · 某时刻前有效”）。',
        en: 'The Secrets page is where credentials (API keys, passwords, …) are handed to you point-to-point. Every delivery is one of two basic forms: **one-time** or **time-limited**; a one-time delivery may additionally carry an expiry (shown in the list as “one-time, valid until a given moment”).',
      },
      {
        k: 'warn',
        zh: '**一次性密件取出即焚**：点“取出正文”并确认后，系统里存的那一份就被烧掉，取件窗口一关正文就永远消失——取之前先准备好要粘贴的地方。限时件不焚，到期之前可以反复取出；取件前的确认框会明说你面对的是哪一种。',
        en: '**A one-time delivery burns on retrieval**: after you press “Take” and confirm, the stored copy is destroyed, and once that window closes the text is gone forever — have the place you will paste it into ready before you take it. Time-limited deliveries do not burn and can be taken again until they expire; the confirmation dialog says plainly which kind you are facing.',
      },
      {
        k: 'ul',
        items: [
          {
            zh: '取出的正文只活在那一个窗口里，带出去有两条路：“复制”走剪贴板，“下载为 .txt”落磁盘。两条路是两个不同的泄漏面——剪贴板会进剪贴板历史、可能被剪贴板管理器或云同步带到别的设备；文件会留在磁盘上、可能被索引和备份。按你的环境自选一条，**用完立刻删掉文件或清掉剪贴板**。',
            en: 'The revealed text lives only in that one window, and there are two ways to carry it out: “Copy” goes through the clipboard, “Download as .txt” goes to disk. They are two different leak surfaces — the clipboard enters clipboard history and may be carried to other devices by clipboard managers or cloud sync; a file stays on disk and may get indexed and backed up. Pick whichever fits your environment, and **delete the file or clear the clipboard as soon as you are done**.',
          },
          {
            zh: '一次性件显示“已取走”而取走的不是你 ⇒ 那枚凭据可能已经外泄：去**轮换**它，不要请人重发。“已过期”“已吊销” ⇒ 找签发方问清楚再要。三种失败的善后完全不同，别搞混。',
            en: 'If a one-time delivery shows “taken” and it was not you who took it ⇒ that credential may have leaked: **rotate** it, do not ask for it to be re-sent. “Expired” or “revoked” ⇒ go back to the issuer. The three failures call for completely different follow-ups — do not mix them up.',
          },
          {
            zh: '新签发的每一件都指名收件人；少数早期的密件签给整个组、组里任何人都能取走——取之前先确认那一件是不是给你的。',
            en: 'Every newly issued delivery names its recipient; a few early ones were issued to a whole group, and anyone in that group can take them — check a delivery is meant for you before you take it.',
          },
          {
            zh: '签发方与管理员可以吊销一件还可取的密件；吊销连正文一起清掉（限时件即使被取过、只要还在窗口内也吊得掉）。',
            en: 'The issuer and administrators can revoke a delivery that is still claimable; revocation erases the body with it (a timed delivery can be revoked even after being taken, as long as its window is open).',
          },
        ],
      },
      { k: 'link', to: '/secrets', zh: '打开密件', en: 'Open Secrets' },
    ],
  },

  {
    id: 'threads',
    title: { zh: '十二、组间通信', en: '12. Cross-group threads' },
    blocks: [
      {
        k: 'p',
        zh: '“组间通信”是项目组之间的通信线，**执行团队全员可读、不按当前组过滤**——这一页存在的意义就是让每个成员看得见组与组之间在说什么。也请据此下笔：你在线上写的每一句话，执行团队的每个人都看得见。',
        en: 'Threads are the lines of communication between project groups, **readable by the whole delivery team and not filtered by your current group** — the page exists precisely so every member can see what the groups are saying to each other. Write accordingly: everything posted there is visible to the whole delivery team.',
      },
      {
        k: 'ul',
        items: [
          {
            zh: '一条线谈完了可以**提议归档**：任一在场方发起，随后是一段**反对窗口**——在场任一方点“取消归档”即作废。窗口内**任何人在这条线里说一句话，提议自动取消**（还在谈，就不算谈完了）；到期没人反对，它会自己归档。窗口多长由服务端配置，所以这里不写死天数：**准确的到期时刻就印在那条线的“待归档”徽标上**。',
            en: 'When a thread is finished you can **propose archiving it**: any party already on the thread starts it, and an **objection window** follows — any party can press “Cancel archiving” to void it. **Anyone posting a message during the window cancels the proposal automatically** (if people are still talking, it is not finished); if nobody objects before the deadline it archives itself. The window length is a server setting, so no number of days is quoted here: **the exact deadline is printed on that thread’s “Archiving proposed” chip**.',
          },
          {
            zh: '归档后的线**只读**（没有回复框，那不是坏了）、默认不出现在列表里——打开“显示已归档”就能看到，一条都没丢。要接着谈就**重开**：楼号与全部历史原样保留。',
            en: 'An archived thread is **read-only** (there is no reply box — that is not a fault) and hidden from the list by default; switch on “Show archived” and every one of them is still there. To continue the conversation, **reopen** it: every message and every sequence number is kept exactly as it was.',
          },
          {
            zh: '发起新线、回帖、把别的组拉进一条线，仅限**联络人**；消息以组的名义发出。没有联络权的账号根本看不到这些按钮——那不是页面坏了，是这个动作本就不属于你。',
            en: 'Starting a thread, replying, and inviting another group into a thread are for **liaisons** only; messages are posted in a group’s name. Without that right you simply do not see those buttons — the page is not broken, the action is just not yours to take.',
          },
          {
            zh: '一条线可以有多个参与组，参与组的徽标全部列在行上，不折叠。',
            en: 'A thread can have several participating groups, and every one of them is shown on the row, never collapsed away.',
          },
          {
            zh: '外包、外部顾问与资方看不到这一页：它是执行层的工作面。外部合作方走工单与合同通道；资方看项目汇报与审批面。',
            en: 'Outsourced partners, external consultants and principals do not see this page: it is the delivery team’s working surface. External partners use tickets and the contract channel; principals read the project reports and the approvals surface.',
          },
        ],
      },
      { k: 'link', to: '/threads', zh: '打开组间通信', en: 'Open Threads' },
    ],
  },

  {
    id: 'reports',
    title: { zh: '十三、项目汇报（只读）', en: '13. Project reports (read-only)' },
    blocks: [
      {
        k: 'p',
        zh: '“汇报”页列出当前项目组的汇报文档：清单、单篇正文与最近修订。它是**只读**的——写入发生在项目侧（对方经命令行提交并留痕，经谁的凭据、哪个工具写的都对得上），所以页面上没有任何写入控件，这不是缺功能。切换当前组，看到的汇报也随之切换。',
        en: 'The Reports page lists your current project group’s report documents: the list, each document’s body, and its recent revisions. It is **read-only** — writing happens on the project side (submitted through the command line, with credential and tool attribution kept), so the page has no writing controls at all; that is not a missing feature. Switch your current group and the reports switch with it.',
      },
      { k: 'link', to: '/reports', zh: '打开汇报', en: 'Open Reports' },
    ],
  },

  {
    id: 'groups',
    title: { zh: '十四、组切换与外来组文档', en: '14. Switching groups, and foreign groups’ documents' },
    blocks: [
      {
        k: 'ul',
        items: [
          {
            zh: '属于多个项目组的账号可以切换“当前组”：入口在右上角头像菜单里（宽屏上点组名小徽章也会打开同一个菜单）。只属一个组的账号没有切换器——没有什么可选。',
            en: 'An account that belongs to several project groups can switch its “current group”: the switcher is in the avatar menu at the top right (on wide screens, clicking the group chip opens the same menu). A single-group account has no switcher — there is nothing to choose.',
          },
          {
            zh: '按组分片的页面跟着当前组走：文档是**每组一棵树**，汇报与工作项也按当前组取数。工时页的日程表是你本人的全量记录、不随切组变化——只有登记时的可选工单清单跟着当前组。',
            en: 'Pages sliced by group follow the current group: Docs is **one tree per group**, and Reports and Work items load for the current group. The Work hours calendar is your own full record and does not change with the group switch — only the ticket picker used when logging follows the current group.',
          },
          {
            zh: '有些组是外来（合作方）的项目组：它们的文档是从对方仓库收来的**只读快照**，多数只有中文原文。英文界面下打开这类文档，页面会直接显示中文原文并提示这一篇只有原文——那不是加载错误，也没有藏着的英文版。',
            en: 'Some groups are foreign (partner) project groups: their documents are **read-only snapshots** collected from the partner’s repository, mostly available in the Chinese original only. Opening such a document under the English interface shows the original directly, with a notice saying only the original exists — that is not a loading error, and there is no hidden English version.',
          },
        ],
      },
    ],
  },

  // ── 管理面章节（SB-151，需求方裁①：同页全员可见）────────────────────────────
  // 独立成组、排在末尾；第一块 note 就说明"仅管理员可操作"。描述不含任何需要权限
  // 才该知道的信息——它们回答的是成员的问题："管理员能替我做什么、该找谁做什么"。
  {
    id: 'admin',
    title: { zh: '十五、管理面：总览', en: '15. The admin surface: overview' },
    short: { zh: '十五、管理总览', en: '15. Admin overview' },
    blocks: [
      {
        k: 'note',
        zh: '本节与后面几节说的功能**只有管理员能操作**。写在人人可见的手册里，是为了让每个成员说得出“管理员能替我做什么、该找谁做什么”。',
        en: 'This and the following sections describe things **only administrators can do**. They live in the manual everyone reads, so that every member can name what an administrator can do for them and whom to ask.',
      },
      {
        k: 'ul',
        items: [
          {
            zh: '管理面挂在 /admin 下，页内有子页签。账号管理：建账号、改岗位/昵称/状态、重置密码、清除某人当天的验证码发送限额（“登录不进来找管理员”背后就是这一步）。',
            en: 'The admin surface lives under /admin, with sub-tabs. Accounts: creating accounts, changing job post / display name / status, resetting passwords, and clearing someone’s login-code quota for the day (this is the step behind “contact an administrator if you cannot sign in”).',
          },
          {
            zh: 'Git 访问：给外协人员签发或吊销源码仓凭据（明文只出现一次）。模型设置：各处理步骤所用模型的覆盖值。源码仓：只读浏览代码仓。运行日志：查询系统运行日志。工时燃耗：全员工时的对账表。',
            en: 'Git access: issuing or revoking source-repository credentials for external collaborators (the plaintext appears exactly once). Model settings: per-step model overrides. Repos: read-only source browsing. Logs: querying the system’s runtime logs. Burn: the work-hours reconciliation table.',
          },
        ],
      },
    ],
  },

  {
    id: 'admin-keys',
    title: {
      zh: '十六、管理面：API 密钥、onboarding 快捷发送与投递归因',
      en: '16. Admin: API keys, onboarding quick-send, and delivery attribution',
    },
    short: { zh: '十六、API 密钥', en: '16. API keys' },
    blocks: [
      {
        k: 'p',
        zh: '“API 密钥”页代签成员的命令行密钥——明文只出现一次，丢了只能吊销重签（成员侧的样子见第七节）。',
        en: 'The API keys page is where members’ command-line keys are issued — the plaintext appears exactly once, and a lost key can only be revoked and re-issued (section 7 describes the member side).',
      },
      {
        k: 'ul',
        items: [
          {
            zh: '**onboarding 快捷发送**：一枚已签发、从未被使用、且仍然生效的密钥，行上有“发 onboarding 站内信”按钮——给收件人发一封教他接入命令行的**系统模板信**（带“系统信”标记、不排队）。正文由服务端按审定模板渲染，绝不含密钥明文。',
            en: '**Onboarding quick-send**: a key that was issued, never used, and is still live shows a “Send onboarding message” button on its row — it sends the recipient a **system template message** (with the “system” badge, no queueing) walking them through connecting the command line. The body is rendered server-side from the reviewed template and never contains the key plaintext.',
          },
          {
            zh: '**内容投递的 key 级归因**：经命令行投递进来的内容包会自动记下用的是哪枚密钥，读面把它显示为该密钥的标注名。它由凭据推导、不由人填——事后要对“这包是谁的凭据投的”，看这一处。',
            en: '**Key-level attribution on content deliveries**: a content package delivered through the command line automatically records which key was used, and the read surface shows that key’s label. It is derived from the credential, never typed in by anyone — when you later need to know whose credential delivered a package, this is where you look.',
          },
        ],
      },
    ],
  },

  {
    id: 'due-actions',
    title: { zh: '十七、管理面：到期动作', en: '17. Admin: due actions' },
    short: { zh: '十七、到期动作', en: '17. Due actions' },
    blocks: [
      {
        k: 'p',
        zh: '“到期动作”记的是“某天必须人工做一次”的事。到了那天它不会自己消失：管理员的页面顶部会出现横幅点名（今天有几件、最久的迟了几天），按钮通向落定页；在那里选“已做”或“放弃”，可以留一句话说明（不强制，但复盘只有这一句可看）。工作空间也可以在它自己的通道上落定任何一条，但必须写明依据；打开“连已落定的一起看”，每条已落定的行上都标着落定者——人（在本页上点的）或工作空间（行上那句话就是它写的依据）。页面上刻意没有“新建”按钮——立行发生在工作空间写裁决记录的那个回合，不靠人记得来建。每行的触发日带帧：日期后带 Z 的按 UTC 日比，标“系统时区日”的按 Asia/Shanghai 的日历日比；页面顶部同时给出两个“今天”。',
        en: 'A due action is something that must be done by hand on a given day. When the day comes it does not go away by itself: a banner appears at the top of administrators’ pages naming what is due (and how late the oldest is), with a button to the settlement page; there it is settled as “done” or “abandoned”, optionally with a sentence of explanation (not enforced, but that sentence is all a later review will have). The workspace may also settle any row through its own channel, but it must state the basis; with “Include settled” on, every settled row names who settled it — a person (clicked on this page) or the workspace (the sentence on the row is its stated basis). There is deliberately no “new” button on the page — rows are created in the workspace at the moment a ruling is recorded, not by someone remembering to add them. Each row’s day carries a frame: a date ending in Z is compared on the UTC day, one marked “system-timezone day” on the Asia/Shanghai calendar day; both todays are shown at the top of the page.',
      },
    ],
  },

  {
    id: 'admin-audit',
    title: { zh: '十八、管理面：敏感读面的读取记录', en: '18. Admin: the read audit' },
    short: { zh: '十八、读取记录', en: '18. Read audit' },
    blocks: [
      {
        k: 'p',
        zh: '“读取记录”页回答“这个资源被谁读过”：按资源查询，读到的人与被拒的试探分开列。留痕本身一直在写，这一页是把它**查出来**的地方——查不到的审计等于没存。成员该知道的一半是：敏感读面上的读取是留痕的。',
        en: 'The read-audit page answers “who has read this resource”: query by resource, with successful readers and refused attempts listed separately. The trail was always being written; this page is where it can actually be **queried** — an audit you cannot query might as well not exist. The half every member should know: reads on sensitive surfaces leave a trail.',
      },
    ],
  },

  {
    id: 'admin-escalations',
    title: { zh: '十九、管理面：agent 逃生口', en: '19. Admin: agent escalations' },
    short: { zh: '十九、逃生口', en: '19. Escalations' },
    blocks: [
      {
        k: 'p',
        zh: '各项目的 agent 做不了某件事时，会走一条正式的“我做不了”通道上报。管理面把这些上报跨项目横切着列出来：工单号、agent 的原话、按原因分类的计数。某类缺料反复出现时，只有在这一页才看得出来。',
        en: 'When a project’s agent cannot do something, it reports through a formal “I cannot do this” channel. The admin surface lists those reports across all projects: the ticket number, the agent’s own words, and counts by reason. A recurring kind of missing input only becomes visible on this page.',
      },
    ],
  },

  // SB-190 §7.3（第二百四十六批）：响应时限的读面与裁决台。写给成员看的那一半是：
  // 「量我的尺子长什么样、我在命令行看到的行与管理员看到的逐字同形」。
  {
    id: 'admin-response-sla',
    title: { zh: '二十、管理面：响应时限', en: '20. Admin: response SLA' },
    short: { zh: '二十、响应时限', en: '20. Response SLA' },
    blocks: [
      {
        k: 'p',
        zh: '“响应时限”页是合同里那 10 分的读面：期间内每一件要求具名当事人响应的事项（指派单、待补充信息、标了“要求响应”的站内信），是不是在 **1 个工作日**内得到了首次实质响应。时钟按 America/Vancouver 的工作日现算（工作日内标记 ⇒ 次一工作日同一钟点；非工作日标记 ⇒ 下一工作日 00:00 起算；同一工作日第 4 件起再顺延一个工作日），候选由机器列出，**“哪一条算实质”由人裁**——“收到，稍后回”永远不会被机器自动计成达标。裁决是追加式台账里新的一行，裁错了再落一条，没有改与删。成员该知道的一半是：你在命令行 `sla mine` 看到的行，与管理员在这一页看到的**逐字同形**——分母、候选、裁决与理由都在，量你的尺子不藏在任何人的抽屉里。',
        en: 'The response-SLA page is the read face of those 10 contract points: for every item in the window that asked a named party for a response (assigned tickets, information requests, messages marked “response required”), was there a first substantive response within **one working day**. The clock runs on America/Vancouver working days (marked on a working day ⇒ the same wall clock next working day; marked on a non-working day ⇒ from 00:00 of the next working day; the fourth item of one working day and later ⇒ one more working day), the machine lists candidates, and **which one counts as substantive is a person’s ruling** — “got it, will reply later” is never counted as met by the machine. A ruling is a new row in an append-only ledger; rule again to correct one, nothing is edited or deleted. The half every member should know: the rows you see in `sla mine` on the command line are **literally** the rows the administrator sees here — denominator, candidates, rulings and reasons included; the ruler you are measured with is not kept in anyone’s drawer.',
      },
    ],
  },
  {
    id: 'asset-progress',
    title: { zh: '二十一、资产对账：四态记录', en: '21. The asset ledger: four-state records' },
    short: { zh: '二十一、资产对账', en: '21. Asset ledger' },
    blocks: [
      {
        k: 'p',
        zh: '“资产对账”页是美术—程序资产对账表的应用面：同一棵巢状树（面 → 类型 → 资产行），每一格是该资产在该态（美术已交／程序已收／程序已接／画面已验）的最新一条记录——值、记录人、日期、证据。缺省全部收起，先看每一面的进度，再点开要记的那一面。',
        en: 'The asset-ledger page is the application face of the art–program asset ledger: the same nested tree (family → type → asset row), where each cell is the latest record of that asset in that state (art delivered / program received / program integrated / screen verified) — value, recorder, date, evidence. Everything starts collapsed: read each family’s progress first, then open the one you need to record in.',
      },
      {
        k: 'ul',
        items: [
          {
            zh: '谁能记什么由岗位决定，页面顶上那枚标签写着本会话的角色：美术记“美术已交”，程序记另外三态，管理员四态皆可；只读岗位看不到“记”按钮。真正的闸在服务端，页面只是照它显隐。',
            en: 'Who may record what follows the job post; the chip at the top names this session’s role: art records “art delivered”, program records the other three, administrators all four; read-only posts see no “Record” button. The real gate is on the server — the page only mirrors it.',
          },
          {
            zh: '记一条就是追加一条带证据的记录，不是改状态：证据必填（工单号、站内信号、截图路径……一句可查的东西），旧记录留在“历史”抽屉里。别人刚记过一条时会提示刷新再记。',
            en: 'Recording appends one record with evidence; it never edits a state: evidence is required (a ticket number, message id, screenshot path — something checkable), and earlier records stay in the “History” drawer. If someone recorded in between, the page asks you to reload and record again.',
          },
          {
            zh: '正典在仓内：工作空间按批把这里的记录拉回台账，文档面的对账表 §5 随之更新；这一页的行来自台账主表的镜像，随部署刷新。',
            en: 'The canon lives in the repository: the workspace pulls the records back into the ledger per batch and the ledger’s §5 on the document face follows; the rows here come from a mirror of the main table, refreshed at deployment.',
          },
        ],
      },
    ],
  },
  {
    id: 'feature-progress',
    title: { zh: '二十二、功能对账：三态记录', en: '22. The feature ledger: three-state records' },
    short: { zh: '二十二、功能对账', en: '22. Feature ledger' },
    blocks: [
      {
        k: 'p',
        zh: '“功能对账”页是功能代码妥善率表的应用面：功能点按启用版本分组，每一格是该功能点在该态（实现／自测／QA）的最新一条记录（值 · 记录人 · 日期 · 证据）。程序岗位在这里自报进度，管理员可代记；资方简报里的“功能实现率”“自测通过率”就从这些记录算出。',
        en: 'The feature-ledger page is the application face of the feature-code readiness table: feature points are grouped by in-use version, and each cell is the latest record of that feature point in that state (implemented / self-tested / QA) — value · recorder · date · evidence. Program posts report their own progress here and administrators may record on their behalf; the investor brief’s “implementation rate” and “self-test pass rate” are computed from these records.',
      },
      {
        k: 'ul',
        items: [
          {
            zh: '谁能记什么由岗位决定：程序记“实现”与“自测”，质量记“QA”，管理员三态皆可；只读岗位看不到“记”按钮。',
            en: 'Who may record what follows the job post: program records “implemented” and “self-tested”, quality records “QA”, administrators all three; read-only posts see no “Record” button.',
          },
          {
            zh: '“已实现”和“通过”必须带证据，证据建议锚定 git 提交（sha、PR）——填的不像提交时页面会提示但不拦；“未开始”“部分”“未”不需要证据，承认没做完不需要证明。',
            en: '“Implemented” and “passed” require evidence, ideally anchored to a git commit (a sha or PR) — the page hints when the evidence does not look like one but does not block; “not started”, “partial” and “none” need no evidence: admitting unfinished work needs no proof.',
          },
          {
            zh: '记一条就是追加一条记录，不是改状态：旧记录留在“历史”抽屉里；别人刚记了一条时你的提交会被拒（刷新再记）。正典在仓内，工作空间按批拉回，文档面的功能表随之更新。',
            en: 'Recording appends a record; it never edits a state: earlier records stay in the “History” drawer, and a submission is refused when someone recorded in between (reload and record again). The canon lives in the repository; the workspace pulls per batch and the feature table on the document face follows.',
          },
        ],
      },
    ],
  },
];

// ── 取文本 ────────────────────────────────────────────────────────────────────
// 未提供 en 时回落 zh（与 i18n/index.mjs 的 t() 同一条回落规则）：宁可让英文读者看到
// 中文原文，也不要让页面上出现一个空段落——空段落读起来像“这里本来该有东西但坏了”。
export function pickText(node, lang) {
  if (!node) return '';
  const v = lang === 'en' ? node.en : node.zh;
  if (typeof v === 'string' && v.trim()) return v;
  return typeof node.zh === 'string' ? node.zh : '';
}

// ── 行内强调 ──────────────────────────────────────────────────────────────────
// 正文里用 **…** 标出“最容易读漏、读漏就要付代价”的那几句（撤回不可逆、建单后仍可补传）。
// 手册全篇是纯文本数据，不引 markdown 渲染器（那是给 data_content 的发布管线用的，
// 为一个加粗把整条链路搬进 SPA 不划算）；这里只切一层加粗，JSX 照着片段渲染即可。
// 成对的 ** 才算标记：落单的一个原样保留——宁可屏幕上出现两个星号，也不要吞掉半句话。
export function inlineSegments(text) {
  const s = typeof text === 'string' ? text : '';
  const parts = s.split('**');
  // 偶数个分隔符（奇数段）才是配平的；不配平就整段当普通文本，不做任何吞字。
  if (parts.length < 3 || parts.length % 2 === 0) return [{ text: s, bold: false }];
  return parts
    .map((text_, i) => ({ text: text_, bold: i % 2 === 1 }))
    .filter((seg) => seg.text !== '');
}

// 页首标题与导语。放在这里而不是 i18n 词典：手册的每一个字都由本模块负责，
// 界面上只借走导航栏那一个键。
export const HELP_TITLE = { zh: '使用手册', en: 'User manual' };
export const HELP_INTRO = {
  zh: '本页讲的是**这套系统**怎么用（登录、文档、工单、附件、工时、站内信、密件……），不是 MirrorMii 的产品文档。任何登录账号都能读到它，与岗位无关。末尾几节讲的是仅管理员可操作的功能——写在这里是让每个成员说得出「管理员能替我做什么」。',
  en: 'This page is about using **the system itself** (signing in, documents, tickets, attachments, work hours, messages, secret deliveries, …). It is not MirrorMii product documentation, and every signed-in account can read it regardless of job post. The final sections describe admin-only features — kept here so every member can name what an administrator can do for them.',
};

// 目录：章节顺序即阅读顺序。渲染侧据此生成页内锚点。
// chip label 取 `short ?? title`（证伪评审：五个管理章标题都以「管理面：」开头，
// MUI Chip 截断后窄屏上区分度趋零——目录用短名，全称留给章标题本身）。
export function sectionToc(lang) {
  return HELP_SECTIONS.map((s) => ({
    id: s.id,
    title: pickText(s.short ?? s.title, lang),
  }));
}

// 附件上限那一行**直接复用**提交表单的话术生成器（由 ATTACH_KINDS 表拼装）。
// 手册不另写一句“可以传什么”——D3 的教训就是那第二份手写话术。
export function attachLimitsLine(t) {
  return attachHelperText(t);
}

// 手册里出现的每一条状态，都必须是 ticketMeta.mjs 认得的状态（否则状态徽章会退化成
// 打印原始英文串）。导出给测试用，也让“手册引用了一个不存在的状态”这件事可判定。
export function referencedStatuses() {
  return [...HELP_STATUS_FLOW, ...HELP_INCIDENT_FLOW, ...HELP_ASSIGNED_FLOW].map((s) => s.status);
}

// 同理：手册的类型表必须与前端类型清单**同集合**。
export function referencedTypes() {
  return HELP_TICKET_TYPES.map((r) => r.type);
}

// 遍历全部可见文本节点（章节标题、块、列表项、求助卡片），供测试做双语完备性检查。
// 之所以要一个遍历器而不是逐块断言：新增一节时最容易漏的就是 en 那一半，
// 而漏了也照样渲染（pickText 回落中文），肉眼看中文界面永远发现不了。
export function allTextNodes() {
  const out = [HELP_TITLE, HELP_INTRO, HELP_ASK.title, HELP_ASK.lead, HELP_ASK.cta, ...HELP_ASK.notes];
  for (const row of HELP_TICKET_TYPES) out.push(row);
  for (const row of HELP_STATUS_FLOW) out.push(row);
  for (const row of HELP_INCIDENT_FLOW) out.push(row);
  for (const row of HELP_ASSIGNED_FLOW) out.push(row);
  for (const s of HELP_SECTIONS) {
    out.push(s.title);
    for (const b of s.blocks) {
      if (Array.isArray(b.items)) out.push(...b.items);
      else if (typeof b.zh === 'string' || typeof b.en === 'string') out.push(b);
    }
  }
  return out;
}

// 渲染侧认得的块类型。写成常量而不是散在 JSX 的 switch 里：新增一种块忘了加渲染分支，
// 页面上就是**静默少一段**——这条清单让那件事在单测里可判定。
export const HELP_BLOCK_KINDS = ['p', 'ul', 'ol', 'note', 'warn', 'link', 'ticketTypes', 'statusFlow', 'incidentFlow', 'assignedFlow', 'attachLimits'];

// 供测试断言引用完整性用（这两个是被镜像的上游表）。
export const HELP_UPSTREAM = { STATUS_ORDER, TICKET_TYPES };

// ── SB-151：路由 → 手册章节的**单一映射表** ─────────────────────────────────
// 每个页面页头那枚 ❓（components/HelpLink.jsx）指向 `/help#<sectionId>`；页与章的
// 对应只在这一张表上。它同时是**覆盖闸**的判据输入：helpLinks.test.mjs 解析 App.jsx
// 的路由表，要求每个页面路由要么在这里、要么在 HELP_PAGE_EXEMPT 里，且映射指向的
// 章节 id 真实存在——本单的病根正是"章节覆盖面没有闸"：手册停在第一百零九批而 15 批
// 新功能零覆盖、无一判据变红。删掉这里任何一行，对应路由当场失覆盖，测试红。
// 键是 App.jsx 里 Route 的 path 原文（含 /docs 的 splat 形），值是 HELP_SECTIONS 的 id。
export const HELP_PAGE_MAP = {
  '/login': 'signin',          // 登录页不挂 ❓（/help 在守卫后，未登录点了只会被弹回），
  '/login/link': 'signin',     // 但覆盖闸照样要求它有章可指——它的章节就是"登录"。
  '/docs': 'docs',
  '/docs/:group/*': 'docs',
  '/submit': 'ticket-types',
  '/my-tickets': 'lifecycle',
  '/approvals': 'lifecycle',   // 审批面没有独立章：它处理的正是"提交之后"那条线的另一端。
  '/work-hours': 'work',
  '/work-items': 'work',
  '/reports': 'reports',
  '/due-actions': 'due-actions',
  '/asset-progress': 'asset-progress',   // 第二百九十二批（SB-197 S3b）
  '/feature-progress': 'feature-progress',   // 第三百零八批（SB-201 S3）
  '/threads': 'threads',
  '/messages': 'messages',
  '/secrets': 'secrets',
  '/cli': 'cli',
  '/admin': 'admin',
  '/admin/git': 'admin',
  '/admin/api-keys': 'admin-keys',
  '/admin/settings': 'admin',
  '/admin/repos': 'admin',
  '/admin/logs': 'admin',
  '/admin/burn': 'admin',
  '/admin/escalations': 'admin-escalations',
  '/admin/read-audit': 'admin-audit',
  '/admin/response-sla': 'admin-response-sla',
};

// 覆盖闸的**封闭**豁免名单：只有手册页自己（它就是手册，❓ 指向自己毫无意义）。
// 名单用 deepEqual 钉死——往这里塞新页面躲覆盖闸，测试当场红，改动必须过评审。
export const HELP_PAGE_EXEMPT = ['/help'];
